/* eslint-disable import/no-internal-modules */
import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { EmptyStateIcon, Image } from 'components/atoms/empty-state';
import { Caption01 } from 'components/styles';
import { TranslationKey, useTranslation } from 'hooks/use-translation';
import { ReactComponent as Blocked } from './blocked.svg';
import { ReactComponent as NoChart } from './no-chart.svg';
import { ReactComponent as Early } from './rabbit.svg';
import { ReactComponent as TableNoData } from './table-no-data.svg';
import { ReactComponent as Delayed } from './turtle.svg';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--spacing-05);
  gap: var(--spacing-05);
  width: 280px;
  text-align: left;
  position: sticky;
  left: 0;
  > svg {
    padding: var(--spacing-04) var(--spacing-03);
    width: 80px;
    height: 80px;
  }
`;

const EmptyKeyEventsWrapper = styled(Wrapper)`
  gap: 0;
`;

const Text = styled.p`
  ${Caption01};
  color: var(--gray-70);
`;

interface Props {
  Svg: React.FC<React.SVGProps<SVGSVGElement>>;
  noDataKey?: TranslationKey;
}

export const BaseEmptyState: FC<Props> = ({ Svg, noDataKey }) => {
  const noData = useTranslation(
    noDataKey || 'reports.template_insight.no_data',
  );

  return (
    <Wrapper>
      <Svg />
      <Text>{noData}</Text>
    </Wrapper>
  );
};

export const BaseKeyEventsAndIntervalsEmptyState = ({
  Svg,
  noDataDescriptionKey,
}) => {
  return (
    <EmptyKeyEventsWrapper>
      <Svg />
      <Text>{useTranslation(noDataDescriptionKey)}</Text>
    </EmptyKeyEventsWrapper>
  );
};

export const BaseKeyEventsAndIntervalsTableEmptyState = ({
  image,
  noDataDescriptionKey,
}) => {
  return (
    <EmptyKeyEventsWrapper>
      <Image size="middle">{image}</Image>
      <Text>{useTranslation(noDataDescriptionKey)}</Text>
    </EmptyKeyEventsWrapper>
  );
};

interface EmptyProp {
  noDataKey?: TranslationKey;
}
export const DefaultEmptyChart = ({ noDataKey }: EmptyProp): ReactNode => (
  <BaseEmptyState
    Svg={NoChart}
    noDataKey={noDataKey || 'reports.template_insight.chart.empty'}
  />
);
export const DefaultKeyEventsAndIntervalsEmptyChart = ({
  noDataDescriptionKey,
}): ReactNode => (
  <BaseKeyEventsAndIntervalsEmptyState
    Svg={NoChart}
    noDataDescriptionKey={noDataDescriptionKey}
  />
);

export const DelayedEmptyChart = (): ReactNode => (
  <BaseEmptyState Svg={Delayed} noDataKey="report.chart.empty.delayed" />
);

export const DelayedEmptyTable = (): ReactNode => (
  <BaseEmptyState Svg={Delayed} noDataKey="report.table.empty.delayed" />
);
export const EarlyEmptyChart = (): ReactNode => (
  <BaseEmptyState Svg={Early} noDataKey="report.chart.empty.early" />
);

export const EarlyEmptyTable = (): ReactNode => (
  <BaseEmptyState Svg={Early} noDataKey="report.table.empty.early" />
);

export const DefaultEmptyTable = ({ noDataKey }: EmptyProp): ReactNode => (
  <BaseEmptyState
    Svg={TableNoData}
    noDataKey={noDataKey || 'reports.template_insight.no_data'}
  />
);

export const DefaultEmptyKeyEventsAndIntervalsEmptyTable = ({
  noDataDescriptionKey,
}) => {
  return (
    <BaseKeyEventsAndIntervalsTableEmptyState
      image={<EmptyStateIcon icon={faTelescope} />}
      noDataDescriptionKey={noDataDescriptionKey}
    />
  );
};

export const BlockedEmptyData = (): ReactNode => (
  <BaseEmptyState Svg={Blocked} noDataKey="report.table.empty.blocked" />
);
